import { buildBowelPattern } from './bowelPattern';
import { calendar, dayDetail } from './calendar';
import { buildCards } from './cards';
import { COMPARE_OPTIONS, compareWith, options, refWeek } from './compare';
import { fmy, fr, fromMonthIndex, monthIndex, monthOf, weekEnd, weekStart } from './dates';
import { buildKpis } from './kpis';
import { GUT_TABS } from './palette';
import { buildDetails, buildTrend } from './trend';
import { clampDay, stepWeek, trendWeeks, weekName } from './weeks';

// Composes the whole tab, as the design's renderVals() does: header, score
// cards, Daily Log tabs, then the active tab's KPIs, calendar, day detail,
// trend and sections (plus the Bowel pattern panel on Bowel Health).

export const FOOTNOTE =
  'The daily symptom average uses bloating, abdominal pain, gas and digestive discomfort (1–5). ' +
  'Symptoms not logged on a check-in day count as 0. Days with no check-in are never counted as zero. ' +
  'Calendar colours are display bands, not clinical categories.';

/** Months the calendar may show: from the first fetched day to today. */
export function monthBounds(ctx) {
  return { min: monthIndex(monthOf(ctx.historyStart)), max: monthIndex(monthOf(ctx.today)) };
}

/** `YYYY-MM` of every month from `a` to `b`. */
function monthsBetween(a, b) {
  const out = [];
  let { year, month } = monthOf(a);
  const end = monthIndex(monthOf(b));
  while (year * 12 + month <= end) {
    out.push(`${year}-${String(month + 1).padStart(2, '0')}`);
    month += 1;
    if (month === 12) {
      month = 0;
      year += 1;
    }
  }
  return out;
}

/**
 * The months of diet tracking a view needs (it is fetched a month at a
 * time): the selected and compared weeks for the fibre card, plus, on Nutrition, the trend's weeks, the first logged
 * week and the calendar month.
 * Clipped to the fetched window. Reads no fibre, so a context built without
 * it will do.
 *
 * @param {Parameters<typeof buildView>[1]} state
 * @returns {string[]} `YYYY-MM`, sorted.
 */
export function fibreMonths(ctx, { tab = 'sym', sel, cmp = 'prev', month } = {}) {
  const day = clampDay(ctx, sel || ctx.today);
  const ws = weekStart(day);
  const ref = refWeek(ctx, ws, cmp);
  const spans = [[ws, weekEnd(ws)], [ref, weekEnd(ref)]];
  if (tab === 'nut') {
    spans.push([trendWeeks(ctx, ws)[0], weekEnd(ws)]);
    if (ctx.firstWeek) spans.push([ctx.firstWeek, weekEnd(ctx.firstWeek)]);
    const bounds = monthBounds(ctx);
    const { year, month: m } = fromMonthIndex(Math.min(bounds.max, Math.max(bounds.min, month ?? monthIndex(monthOf(day)))));
    const first = `${year}-${String(m + 1).padStart(2, '0')}-01`;
    spans.push([first, first]);
  }
  const months = new Set();
  spans.forEach(([a, b]) => {
    const start = a < ctx.historyStart ? ctx.historyStart : a;
    const end = b > ctx.today ? ctx.today : b;
    if (start <= end) monthsBetween(start, end).forEach((ym) => months.add(ym));
  });
  return [...months].sort();
}

/**
 * @param {ReturnType<import('./dayIndex').buildContext>} ctx
 * @param {{
 *   tab?: 'sym'|'bowel'|'nut',
 *   sel?: string,                   // selected day (defaults to today); selects its week
 *   cmp?: 'prev'|'first',           // header Compare with
 *   month?: number,                 // calendar month index (defaults to the selected day's)
 *   open?: Record<string, boolean>, // `${tab}-${sectionId}` → open
 *   bcView?: 'week'|'month',        // Bowel pattern view
 *   bcCmp?: 'prev'|'first'|'off',   // Bowel pattern compare
 * }} state
 */
export function buildView(ctx, { tab = 'sym', sel, cmp = 'prev', month, open = {}, bcView = 'week', bcCmp = 'prev' } = {}) {
  const day = clampDay(ctx, sel || ctx.today);
  const ws = weekStart(day);
  const we = weekEnd(ws);
  const c = compareWith(ctx, ws, cmp);
  const bounds = monthBounds(ctx);
  const monthIdx = Math.min(bounds.max, Math.max(bounds.min, month ?? monthIndex(monthOf(day))));
  const kpis = buildKpis(ctx, tab, c);

  return {
    sel: day,
    header: {
      since: ctx.firstLog ? fmy(ctx.firstLog) : '',
      wkRange: fr(ws, we),
      wkLabel: `${weekName(ctx, ws)}${ws === ctx.maxWeek ? ' · current' : ''}`,
      prevSel: stepWeek(ctx, day, -1),
      nextSel: stepWeek(ctx, day, 1),
      cmpOpts: options(COMPARE_OPTIONS, cmp),
    },
    cards: buildCards(ctx, c, tab),
    tabs: options(GUT_TABS.map((t) => [t.id, t.name]), tab),
    scope: `${weekName(ctx, ws)} · ${fr(ws, we)}, ${monthOf(we).year}`,
    primary: kpis.primary,
    secondary: kpis.secondary,
    lists: kpis.lists,
    monthIndex: monthIdx,
    cal: calendar(ctx, tab, fromMonthIndex(monthIdx), day, { canPrev: monthIdx > bounds.min, canNext: monthIdx < bounds.max }),
    detail: dayDetail(ctx, tab, day),
    trend: buildTrend(ctx, tab, ws),
    details: buildDetails(ctx, tab, ws, day).map((d) => ({ ...d, isOpen: open[`${tab}-${d.id}`] ?? d.defaultOpen })),
    bowel: tab === 'bowel' ? buildBowelPattern(ctx, { sel: day, view: bcView, cmp: bcCmp }) : null,
    footnote: FOOTNOTE,
  };
}
